import { Flex, Image, Td, Text, Th, Tr, useDisclosure } from '@chakra-ui/react';
import { BizOverviewOfferFragment } from '@client/graphql/__generated__/types';
import { format } from 'date-fns';
import isNil from 'lodash/isNil';
import { useState } from 'react';
import { BasicCardTable } from '~/common/components/BasicCardTable/BasicCardTable';
import { formatToCurrency } from '~/services/document/utils/number';
import {
  DEFAULT_PROPERTY_IMAGE_URL,
  getPropertySource,
} from '../../../../utils/listing.utils';
import { UserAvatar } from '../../../User/UserAvatar';
import { ViewOfferDrawer } from './ViewOfferDrawer/ViewOfferDrawer';

const formatPurchasePrice = (value?: number) => {
  if (isNil(value)) return;

  const notation = value >= 1_000_000 ? 'compact' : 'standard';

  // Properly format millions values with 2 decimal places
  // 1,950,000 should be 1.95M but 19,500,000 should be 19.5M
  const millionsValue = value / 1000000;
  const decimals =
    value >= 1000000
      ? millionsValue < 10
        ? 2
        : millionsValue < 100
          ? 1
          : 0
      : 0;

  return formatToCurrency(value, decimals, notation);
};

interface BusinessOverviewOffersTableProps {
  businessOverviewOffers: BizOverviewOfferFragment[];
  loading: boolean;
  fetchTriggerRef: (ref: HTMLDivElement) => void;
}

export const BusinessOverviewOffersTable = ({
  businessOverviewOffers,
  loading,
  fetchTriggerRef,
}: BusinessOverviewOffersTableProps) => {
  const [selectedOffer, setSelectedOffer] =
    useState<BizOverviewOfferFragment | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const body =
    !loading && !businessOverviewOffers?.length ? (
      <Tr>
        <Td borderBottom="none" colSpan={100}>
          <Text fontSize="xl" fontWeight="medium" textAlign="center">
            No Offers yet
          </Text>
        </Td>
      </Tr>
    ) : (
      <>
        {businessOverviewOffers.map((offer) => {
          const address = offer.property?.address;
          const purchasePrice = offer.purchasePrice;

          const agent = offer.agent;

          return (
            <Tr
              key={offer.id}
              _hover={{ bg: 'whiteAlpha.100' }}
              cursor="pointer"
              height={{ base: '60px', md: 'auto' }}
              onClick={() => {
                setSelectedOffer(offer);
                onOpen();
              }}
            >
              <Td whiteSpace="nowrap">
                <Flex alignItems="center" gap={2} minWidth="max-content">
                  {agent && <UserAvatar size="sm" user={agent} />}
                  <Text overflow="hidden" textOverflow="ellipsis">
                    {agent?.fullName}
                  </Text>
                </Flex>
              </Td>

              <Td>
                <Flex alignItems="center" gap={2} minWidth="max-content">
                  <Image
                    alt="Property"
                    borderRadius="md"
                    fallbackSrc={DEFAULT_PROPERTY_IMAGE_URL}
                    height="35px"
                    objectFit="cover"
                    src={getPropertySource(offer.property?.coverPhotoUrl)}
                    width="50px"
                  />
                  <Flex direction="column">
                    <Text
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {address?.streetAddress}
                      {address?.unit ? ` ${address.unit}` : ''}
                    </Text>
                    <Text
                      color="whiteAlpha.600"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {address?.city} {address?.state} {address?.zipCode}
                    </Text>
                  </Flex>
                </Flex>
              </Td>
              <Td>
                <Flex alignItems="center" gap={2} minWidth="max-content">
                  <Text
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {offer.buyerNames.join(', ')}
                  </Text>
                </Flex>
              </Td>
              <Td whiteSpace="nowrap">
                {purchasePrice ? formatPurchasePrice(purchasePrice) : '-'}
              </Td>
              <Td whiteSpace="nowrap">
                {format(new Date(offer.createdAt as string), 'MMM dd, yyyy')}
              </Td>
              <Td whiteSpace="nowrap">
                <Text textTransform="capitalize">
                  {(offer.status || '').toLowerCase()}
                </Text>
              </Td>
            </Tr>
          );
        })}
      </>
    );

  return (
    <Flex
      basis={0}
      direction="column"
      grow={1}
      height="100%"
      minHeight={0}
      width="100%"
    >
      <BasicCardTable
        body={body}
        fetchTriggerRef={fetchTriggerRef}
        header={
          <Tr>
            <Th w={{ base: '120px', md: '20%' }}>Agent</Th>
            <Th w={{ base: '250px', md: '25%' }}>Property</Th>
            <Th w={{ base: '150px', md: '20%' }}>Buyer</Th>
            <Th w={{ base: '120px', md: '15%' }}>Purchase Price</Th>
            <Th w={{ base: '100px', md: '15%' }}>Date Created</Th>
            <Th w={{ base: '100px', md: '10%' }}>Status</Th>
          </Tr>
        }
        loading={loading}
      />
      {selectedOffer && (
        <ViewOfferDrawer
          isOpen={isOpen}
          offer={selectedOffer}
          onClose={onClose}
        />
      )}
    </Flex>
  );
};
